import { Injectable, inject } from '@angular/core';
import { IPromise, IQService, ITimeoutService } from 'angular';
import { Q_PORT, TIMEOUT_PORT } from '../utils';

interface CardData {
  number: string;
  cvc: string;
  exp_month: string;
  exp_year: string;
  zip: string;
}

interface AddressData {
  streetAddress1: string;
  streetAddress2: string;
  country: string;
  city: string;
  state: string;
  zip: string;
}

interface PaymentModel {
  cvc: string;
  number: string;
  exp_month: string;
  exp_year: string;
  address: string;
  zip: string;
  acmeToken: string;
  ccLastFourDigits: string;
}

interface Validator {
  field: string;
  len: { message: string; test: { value: string } };
}

interface ErrorCodeEntry {
  type: string;
  code: number;
}

/**
 * PaymentProcessorService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/paymentProcessor/paymentProcessor.service.js
 * Processes credit card payments with validation and token generation.
 */
@Injectable({ providedIn: 'root' })
export class PaymentProcessorService {
  private q = inject<IQService>(Q_PORT);
  private timeout = inject<ITimeoutService>(TIMEOUT_PORT);

  model: Partial<PaymentModel> = {};

  readonly card: CardData = {
    number: '',
    cvc: '',
    exp_month: '',
    exp_year: '',
    zip: '',
  };

  readonly address: AddressData = {
    streetAddress1: '',
    streetAddress2: '',
    country: '',
    city: '',
    state: '',
    zip: '',
  };

  readonly fields = {
    number: 'cc-number',
    cvc: 'cc-cvc',
    exp_month: 'cc-month',
    exp_year: 'cc-year',
    streetAddress1: 'streetAddress1',
    streetAddress2: 'streetAddress2',
    country: 'country',
    city: 'city',
    state: 'state',
    zip: 'zip',
    address: 'address',
    errors: {
      number: 'Invalid credit card number',
      cvc: 'Invalid CVC code',
      exp_month: 'Invalid expire month',
      exp_year: 'Invalid expire year',
      streetAddress1: 'Invalid street address',
      country: 'Invalid country',
      city: 'Invalid city',
      state: 'Invalid state',
      zip: 'Invalid zip/postal code',
      address: 'Invalid address',
    },
  };

  readonly CODES = {
    errors: {
      INVALID_NUMBER: 6100,
      INVALID_EXPIRY_MONTH: 6101,
      INVALID_EXPIRY_YEAR: 6102,
      INVALID_CVC: 6103,
      INCORRECT_NUMBER: 6104,
      EXPIRED_CARD: 6105,
      INCORRECT_CVC: 6106,
      INCORRECT_ZIP: 6107,
      INCORRECT_ADDRESS: 6113,
      CARD_DECLINED: 6108,
      MISSING: 6109,
      PROCESSING_ERROR: 6110,
      STREET_ADDRESS1_REQUIRED: 5082,
      CITY_REQUIRED: 5083,
      STATE_REQUIRED: 5084,
      ZIP_REQUIRED: 5085,
      COUNTRY_REQUIRED: 5086,
    },

    find: (code: string | number): ErrorCodeEntry | false => {
      const codeNum = parseInt(String(code), 10);
      const errors = this.CODES.errors;

      if (!isNaN(codeNum)) {
        for (const type in errors) {
          if (Object.prototype.hasOwnProperty.call(errors, type) && (errors as any)[type] === codeNum) {
            return {
              type,
              code: (errors as any)[type],
            };
          }
        }
      }

      return false;
    },
  };

  qDefer: any = false;

  // ─── Initialization ───────────────────────────────────────────────────────

  init(): boolean {
    return true;
  }

  // ─── Card management ──────────────────────────────────────────────────────

  clear(): void {
    this.card.number = '';
    this.card.cvc = '';
    this.card.exp_month = '';
    this.card.exp_year = '';
    this.card.zip = '';
    this.model = {};
  }

  // ─── Token generation ─────────────────────────────────────────────────────

  generateToken(): string {
    const cc = this.card.number.replace(/ /g, '');
    const first = cc.substr(0, 6);
    const last = cc.substr(cc.length - 4, 4);
    return first + last;
  }

  // ─── Payment processing ───────────────────────────────────────────────────

  process(): IPromise<unknown> {
    this.qDefer = this.q.defer();

    this.model = {
      cvc: this.card.cvc,
      number: this.card.number,
      exp_month: this.card.exp_month,
      exp_year: this.card.exp_year,
      address: this.card.number, // Note: legacy assigns number to address
      zip: this.card.zip,
      acmeToken: this.generateToken(),
      ccLastFourDigits: this.card.number.substr(this.card.number.length - 4, 4),
    };

    this.timeout(() => {
      this.qDefer.resolve({ data: { payload: this.model } });
    }, 0);

    return this.qDefer.promise;
  }

  // ─── Error mapping ────────────────────────────────────────────────────────

  mapError(code: string | number): Validator[] {
    const validators: Validator[] = [];
    const e = this.CODES.find(code);

    if (!e) return validators;

    switch (e.type) {
      case 'MISSING':
      case 'EXPIRED_CARD':
      case 'CARD_DECLINED':
      case 'INVALID_NUMBER':
      case 'INCORRECT_NUMBER':
      case 'PROCESSING_ERROR':
        validators.push({
          field: this.fields.number,
          len: { message: 'Please enter a valid credit card number', test: { value: '' } },
        });
        break;
      case 'INVALID_CVC':
      case 'INCORRECT_CVC':
        validators.push({
          field: this.fields.cvc,
          len: { message: 'Invalid CVC code', test: { value: '' } },
        });
        break;
      case 'INVALID_EXPIRY_MONTH':
        validators.push({
          field: this.fields.exp_month,
          len: { message: 'Please enter a valid expiration month', test: { value: '' } },
        });
        break;
      case 'INVALID_EXPIRY_YEAR':
        validators.push({
          field: this.fields.exp_year,
          len: { message: 'Please enter a valid expiration year', test: { value: '' } },
        });
        break;
      case 'INCORRECT_ADDRESS':
        validators.push({
          field: this.fields.address,
          len: { message: 'Address verification failed', test: { value: '' } },
        });
        break;
      case 'INCORRECT_ZIP':
        validators.push({
          field: this.fields.zip,
          len: { message: 'Zip code failed verification', test: { value: '' } },
        });
        break;
    }

    return validators;
  }

  // ─── Response handlers ─────────────────────────────────────────────────────

  success = {
    response: (response: any, status: number) => {
      console.log('PaymentProcessor::success::response', this, response);
    },
  };

  error = {
    response: (response: any, status: number) => {
      console.log('PaymentProcessor::error::response', this, response);
    },
  };
}
